import { useMemo } from 'react';
import useWidth from '../../../components/Hooks/useWidth';
import useGeneralSettingsStore from '../../../stores/GeneralSettings/useGeneralSettingsStore';
import { OrderItemPriceCompositionItem } from '../../orders/order.types';
import { isAllPricesEqual } from '../../orders/products/orderProducts.utils';
import { PricesConfig, ProductPricesRendererPrice } from './interfaces/product-prices.interfaces';
import { emptyPricesConfig, initialPricesState, mapPrices } from './product-prices.utils';

type UseProductPricesRendererProps = {
  prices: ProductPricesRendererPrice[];
  hasOrderItemQuantity?: boolean;
  showAllPrices?: boolean;
};

const checkIfHasDifferentPricesComposition = (
  orderItemPriceCompositionItems: OrderItemPriceCompositionItem[],
) => {
  const idToPricesMap = new Map<string, Set<number>>();

  for (const item of orderItemPriceCompositionItems) {
    const prices = idToPricesMap.get(item.id) || new Set<number>();
    prices.add(item.price);
    idToPricesMap.set(item.id, prices);
  }

  for (const prices of idToPricesMap.values()) {
    if (prices.size > 1) {
      return true;
    }
  }

  return false;
};

const useProductPricesRenderer = ({
  hasOrderItemQuantity,
  prices,
  showAllPrices,
}: UseProductPricesRendererProps) => {
  const { mediumDownWidth } = useWidth();
  const {
    generalSettings: { enableMarkup, enableRetailPrice },
  } = useGeneralSettingsStore();

  const pricesConfig: PricesConfig = useMemo(() => {
    if (prices.length === 0) {
      return emptyPricesConfig;
    }

    if (hasOrderItemQuantity) {
      return prices
        .filter((price) => !!price.orderItemQuantity && price.orderItemQuantity > 0)
        .reduce(mapPrices, initialPricesState);
    }

    return prices.reduce(mapPrices, initialPricesState);
  }, [hasOrderItemQuantity, prices]);

  const hasManyPrices = prices?.length > 0 && !isAllPricesEqual(prices) && showAllPrices;

  const allPriceCompositionItems = prices
    .filter((price) => !!price.orderItemQuantity && price.orderItemQuantity > 0)
    .flatMap(
      (price) => price.priceComposition?.map((priceCompositionItem) => priceCompositionItem) || [],
    );

  const hasManyPricesCompositions = checkIfHasDifferentPricesComposition(allPriceCompositionItems);

  const hasPriceComposition = prices.some(
    (price) => price.priceComposition && price.priceComposition.length > 0,
  );

  const showInfoTooltip = hasManyPrices || hasPriceComposition;
  const showInfoButton = (hasManyPrices || hasPriceComposition) && mediumDownWidth;

  const calcMarkup = () => {
    const { hasPromotion } = pricesConfig;
    const minPromotionalPrice = pricesConfig.minPromotionalPrice || 0;
    const minRetailPrice = pricesConfig.minRetailPrice || 0;
    const minWholesalePrice = pricesConfig.minWholesalePrice || 0;

    if (minRetailPrice <= 0 || minWholesalePrice <= 0) {
      return null;
    }
    return minRetailPrice / (hasPromotion ? minPromotionalPrice : minWholesalePrice);
  };

  return {
    pricesConfig,
    showInfoTooltip,
    showInfoButton,
    showMarkup: enableMarkup,
    showRetailPrice: enableRetailPrice,
    markup: calcMarkup(),
    hasManyPrices: !!hasManyPrices,
    hasManyPricesCompositions,
  };
};

export default useProductPricesRenderer;
